import { normalizeFileinfoIdList } from "../../../utils/fileinfoIds";

/**
 * Read one json-patch value off an activity.
 * @param {object} activity IssbActivityDto
 * @param {string} path e.g. "/sourceSlices"
 */
function patchValue(activity, path) {
  return activity?.jsonPatch?.find((patch) => patch?.path === path)?.value;
}

/**
 * Attachments are published with the answer, so an AI draft's attachments are added to what is
 * already attached rather than replacing it. Mirrors the merge the AI service does when it saves
 * a non-draft answer (backend-python tasks/question_answer.py).
 *
 * @returns {string[]|undefined} undefined when there is nothing to attach
 */
export function mergeAnswerFiles(existingFiles, addedFiles) {
  return normalizeFileinfoIdList([...(existingFiles || []), ...(addedFiles || [])]);
}

/**
 * sourceSlices replace rather than merge -- they describe the provenance of ONE answer.
 * Always a value (`[]` when nothing was cited) so stale slices get cleared: IssbQuestionFacade
 * skips null values, so null would silently leave the previous answer's sources in place.
 */
function answerSources(sourceSlices, existingFiles, addedFiles) {
  const files = mergeAnswerFiles(existingFiles, addedFiles);
  // Note: omit the key when there is nothing to attach, rather than sending files=undefined and
  // losing the optimistic cache update.
  return files ? { sourceSlices: sourceSlices || [], files } : { sourceSlices: sourceSlices || [] };
}

/**
 * The source linkage carried by an AI-draft activity, ready to merge into the question data
 * when that draft is applied.
 *
 * Why this exists: applying a draft used to copy only answer/confidence/completeness, so the
 * draft's provenance and its proposed attachments were dropped, and question.sourceSlices kept
 * whatever it happened to hold from an earlier apply.
 *
 * @param {object} activity IssbActivityDto for the draft being applied
 * @param {object} questionData the question as it currently stands
 * @returns {{sourceSlices: Array, files?: string[]}} spread this into the save payload
 */
export function sourcesFromActivity(activity, questionData) {
  return answerSources(patchValue(activity, "/sourceSlices"), questionData?.files, patchValue(activity, "/files"));
}

/**
 * The fields a BULK apply writes onto a question from an AI draft.
 *
 * Extracted so the provenance half is testable. It is the half that was missing: the bulk apply copied only
 * answer, confidence and completeness, so an answer built entirely from sources was applied with none attached —
 * and nothing said so, because an activity logs a `/sourceSlices` patch only when the value CHANGES, and an
 * empty list replacing an empty list is no change.
 *
 * The single-question Apply Answer button reaches the same result by a different route (it goes through the
 * editor's `setAIAnswer`), which is why this is not shared with it.
 *
 * @param {object} question the question as it currently stands
 * @param {object} draft the IssbActivityDto for the draft being applied
 * @param {{answer: string, confidence: string|undefined, completeness: string|undefined}} applied the
 *        already-converted answer text and statuses, which this function does not derive
 * @returns {object} the full payload to POST
 */
export function bulkApplyPayload(question, draft, applied) {
  return {
    ...question,
    // Clear any parsed multipart version, which would otherwise be re-serialised over the new answer.
    _answerParts: null,
    answer: applied?.answer,
    confidence: applied?.confidence,
    completeness: applied?.completeness,
    ...sourcesFromActivity(draft, question),
  };
}

/**
 * Same, for an answer applied straight from the AI chat (which has the AI's json_content rather
 * than a stored activity). `files` there means files-used (activity-only); the attachments the AI
 * wants published are `attachments`.
 *
 * @param {object} message AI chat message
 * @param {object} questionData the question as it currently stands
 * @returns {{sourceSlices: Array, files?: string[]}}
 */
export function sourcesFromChatMessage(message, questionData) {
  const json = message?.json_content;
  return answerSources(json?.sourceSlices, questionData?.files, json?.attachments);
}
